<script setup lang="ts">
import { useCurrentStudyStore } from '@src/store/dicom-web/current-study-store';
import { computed, ref, watch } from 'vue';

const currentStudyStore = useCurrentStudyStore();

const menuOpen = ref(false);

const weightKg = ref(0);
const heightCm = ref(0);

const studyInfo = computed(() => currentStudyStore.getStudyInfo);
const activeHeaderInfo = computed(
  () => currentStudyStore.getActiveHeaderDisplayInfo,
);

const getValue = (key: any, config: any) => {
  let returnValue = null;
  const typedFallback = config.fallback as keyof typeof studyInfo.value;
  const typedKey = key as keyof typeof studyInfo.value;

  if (
    studyInfo.value
    && typedKey in studyInfo.value
    && studyInfo.value[typedKey]
  ) {
    returnValue = studyInfo.value[typedKey];
  }
  else {
    returnValue = studyInfo.value[typedFallback];
  }
  return returnValue || 'N/A';
};

const UNIT_BY_KEY: Record<string, string> = {
  PatientWeight: 'kg',
  PatientSize: 'cm',
};

const getDisplayValue = (key: any, config: any) => {
  const raw = getValue(key, config);
  if (raw === 'N/A') return raw;
  if (key === 'PatientSize') {
    const cm = Math.round(Number(raw) * 100); // stored in metres → show cm
    return Number.isFinite(cm) ? `${cm} cm` : 'N/A';
  }
  const unit = UNIT_BY_KEY[key];
  return unit ? `${raw} ${unit}` : raw;
};

const allInfoTooltip = computed(() => {
  return Object.entries(activeHeaderInfo.value)
    .map(([key, config]) => `${config.label}: ${getDisplayValue(key, config)}`)
    .join('\n');
});

const isLastChild = (key: any) => {
  const keys = Object.keys(activeHeaderInfo.value);
  return keys.indexOf(key) === keys.length - 1;
};

watch(
  studyInfo,
  (info) => {
    weightKg.value = info?.PatientWeight ?? 0;
    heightCm.value = info?.PatientSize ? Math.round(info.PatientSize * 100) : 0;
  },
  { immediate: true },
);

const saveMeasurements = () => {
  currentStudyStore.setPatientMeasurements(weightKg.value, heightCm.value / 100);
  menuOpen.value = false;
};
</script>

<template>
  <v-menu v-model="menuOpen" :close-on-content-click="false" location="bottom">
    <template #activator="{ props }">
      <div
        class="simple-panel px-4 py-1 flex align-center clickable"
        :title="allInfoTooltip"
        v-bind="props"
      >
        <div class="flex align-center">
          <div
            v-for="(config, key, index) in activeHeaderInfo"
            :key="key"
            class="flex align-center"
          >
            <h6 class="label">
              {{ config.label }}:
            </h6>
            <!-- first value patient name shall not be truncated -->
            <h6 :class="index === 0 ? 'ml-1' : 'ellipsis ml-1'">
              {{ getDisplayValue(key, config) }}
            </h6>
            <h6 v-if="!isLastChild(key)" class="mx-2 divider">
              |
            </h6>
          </div>
        </div>
      </div>
    </template>

    <v-list class="pa-2" width="180">
      <v-text-field
        v-model.number="weightKg" type="number" label="Weight (kg)"
        density="compact" hide-details class="mb-2"
      />
      <v-text-field
        v-model.number="heightCm" type="number" label="Height (cm)"
        density="compact" hide-details class="mb-2"
      />
      <div class="d-flex justify-end">
        <v-btn size="small" color="primary" @click="saveMeasurements">
          Save
        </v-btn>
      </div>
    </v-list>
  </v-menu>
</template>

<style scoped>
.label {
  font-weight: bold;
  color: var(--md-text-secondary);
}
.divider {
  color: var(--md-text-secondary);
}
.ellipsis {
  display: inline-block;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

.clickable {
  cursor: pointer;
}
</style>
